// src/pages/Dashboard/utils/clientDataProcessor.js

import { ERROR_MESSAGES } from './dashboardConstants';

/**
 * Processa e retorna os dados do cliente de forma segura e formatada
 * @param {object} casoSelecionado - Objeto do caso selecionado
 * @param {boolean} debugMode - Se deve incluir informações de debug
 * @returns {string} - Dados do cliente formatados ou mensagem de erro
 */
export const getDadosCliente = (casoSelecionado, debugMode = false) => {
  // Logs de debug sempre ativos no console
  console.log('🔍 getDadosCliente() chamada');
  console.log('🔍 casoSelecionado:', casoSelecionado);
  console.log('🔍 casoSelecionado?.dados_cliente:', casoSelecionado?.dados_cliente);
  console.log('🔍 Tipo de dados_cliente:', typeof casoSelecionado?.dados_cliente);
  
  if (!casoSelecionado) {
    console.log('❌ Caso não selecionado');
    return ERROR_MESSAGES.NO_CASE_SELECTED;
  }
  
  const dadosOriginais = casoSelecionado.dados_cliente;
  
  // Informações de debug visíveis na UI se modo debug ativo
  let debugInfo = '';
  if (debugMode) {
    debugInfo = `=== DEBUG INFO ===
Caso ID: ${casoSelecionado.id}
Campo dados_cliente existe: ${dadosOriginais !== undefined}
Valor original: ${dadosOriginais}
Tipo: ${typeof dadosOriginais}
É null: ${dadosOriginais === null}
É undefined: ${dadosOriginais === undefined}
É string vazia: ${dadosOriginais === ''}
Length: ${dadosOriginais ? dadosOriginais.length : 'N/A'}

=== DADOS REAIS ===
`;
  }
  
  // Verificar se existe dados_cliente
  if (dadosOriginais === null || dadosOriginais === undefined) {
    console.log('❌ dados_cliente é null ou undefined');
    return debugInfo + 'Campo dados_cliente está vazio (null/undefined)';
  }
  
  // Se for string vazia
  if (dadosOriginais === '') {
    console.log('❌ dados_cliente é string vazia');
    return debugInfo + 'Campo dados_cliente está vazio (string vazia)';
  }
  
  // Se for string com apenas espaços
  if (typeof dadosOriginais === 'string' && dadosOriginais.trim() === '') {
    console.log('❌ dados_cliente é string só com espaços');
    return debugInfo + 'Campo dados_cliente contém apenas espaços em branco';
  }
  
  console.log('✅ dados_cliente tem conteúdo, processando...');
  
  // Se for objeto, retornar JSON formatado
  if (typeof dadosOriginais === 'object') {
    console.log('✅ dados_cliente é objeto, convertendo para JSON');
    return debugInfo + JSON.stringify(dadosOriginais, null, 2);
  }
  
  // Se for string, tentar parse JSON
  if (typeof dadosOriginais === 'string') {
    console.log('✅ dados_cliente é string, tentando parse JSON...');
    try {
      const dados = JSON.parse(dadosOriginais);
      console.log('✅ Parse JSON bem-sucedido');
      return debugInfo + JSON.stringify(dados, null, 2);
    } catch (error) {
      console.log('⚠️ Parse JSON falhou, retornando string original');
      return debugInfo + dadosOriginais;
    }
  }
  
  // Para qualquer outro tipo, converter para string
  console.log('⚠️ Tipo não esperado, convertendo para string');
  return debugInfo + String(dadosOriginais);
};

/**
 * Prepara o conteúdo das jurisprudências relacionadas para exibição
 * @param {Array} jurisprudenciasRelacionadas - Array de jurisprudências
 * @returns {string} - Conteúdo formatado
 */
export const prepararConteudoJurisprudencias = (jurisprudenciasRelacionadas) => {
  if (!jurisprudenciasRelacionadas || jurisprudenciasRelacionadas.length === 0) {
    return 'Nenhuma jurisprudência associada a este caso.\n\nUse a seção "Pesquisa de Jurisprudência" para encontrar e associar jurisprudências relevantes.';
  }

  let conteudo = `📚 JURISPRUDÊNCIAS RELACIONADAS (${jurisprudenciasRelacionadas.length})\n\n`;
  
  jurisprudenciasRelacionadas.forEach((juris, idx) => {
    conteudo += `${idx + 1}. PROCESSO: ${juris.processo_numero || 'N/A'}\n`;
    conteudo += `   Tribunal: ${juris.tribunal_origem || 'N/A'}\n`;
    conteudo += `   Data: ${juris.data_acordao ? new Date(juris.data_acordao).toLocaleDateString('pt-PT') : 'N/A'}\n`;
    conteudo += `   Meio Processual: ${juris.meio_processual || 'N/A'}\n`;
    if (juris.relator) {
      conteudo += `   Relator: ${juris.relator}\n`;
    }
    if (juris.decisao) {
      conteudo += `   Decisão: ${juris.decisao}\n`;
    }
    if (juris.url_original) {
      conteudo += `   URL Original: ${juris.url_original}\n`;
    }
    if (juris.sumario) {
      conteudo += `   Sumário: ${juris.sumario.substring(0, 200)}${juris.sumario.length > 200 ? '...' : ''}\n`;
    }
    conteudo += '\n';
  });
  
  return conteudo;
};

/**
 * Prepara o contexto completo do caso para o chat
 * @param {object} casoSelecionado - Caso selecionado
 * @param {object} analise - Análise processada (casoUso, estrategia, fundamentacao)
 * @param {string} dadosClienteStr - Dados do cliente formatados
 * @param {Array} jurisprudenciasRelacionadas - Jurisprudências relacionadas
 * @param {object} user - Dados do usuário/advogado
 * @returns {string} - Contexto formatado para o chat
 */
export const prepararContextoCasoParaChat = (
  casoSelecionado,
  analise,
  dadosClienteStr,
  jurisprudenciasRelacionadas,
  user
) => {
  const { casoUso, estrategia, fundamentacao } = analise;
  
  // Preparar contexto de jurisprudências
  let jurisprudenciasContext = '';
  if (jurisprudenciasRelacionadas && jurisprudenciasRelacionadas.length > 0) {
    jurisprudenciasContext = '\n===== JURISPRUDÊNCIAS RELACIONADAS =====\n';
    jurisprudenciasRelacionadas.forEach((juris, idx) => {
      jurisprudenciasContext += `\n${idx + 1}. PROCESSO: ${juris.processo_numero || 'N/A'}\n`;
      jurisprudenciasContext += `   Tribunal: ${juris.tribunal_origem || 'N/A'}\n`;
      jurisprudenciasContext += `   Data: ${juris.data_acordao ? new Date(juris.data_acordao).toLocaleDateString('pt-PT') : 'N/A'}\n`;
      jurisprudenciasContext += `   Meio Processual: ${juris.meio_processual || 'N/A'}\n`;
      jurisprudenciasContext += `   Relator: ${juris.relator || 'N/A'}\n`;
      jurisprudenciasContext += `   Decisão: ${juris.decisao || 'N/A'}\n`;
      
      if (juris.url_original) {
        jurisprudenciasContext += `   URL Original: ${juris.url_original}\n`;
      }
      
      if (juris.texto_integral) {
        jurisprudenciasContext += `   Texto Integral: ${juris.texto_integral}\n`;
      }
      if (juris.tags && juris.tags.length > 0) {
        jurisprudenciasContext += `   Tags: ${juris.tags.join(', ')}\n`;
      }
      jurisprudenciasContext += '\n';
    });
  }
  
  const contextContent = `
===== CONTEXTO DO CASO JURÍDICO =====
Nome do Caso: ${casoSelecionado.name}
Descrição: ${casoSelecionado.description}

===== ANÁLISE JURÍDICA COMPLETA =====
${casoUso}

${estrategia}

${fundamentacao}

===== DADOS DO CLIENTE =====
${dadosClienteStr}

===== DADOS DO ADVOGADO RESPONSÁVEL =====
Nome: ${user.nome || 'Não informado'}
Número da Ordem dos Advogados: ${user.documento || 'Não informado'}

===== ACORDÃOS ASSOCIADOS AO CASO =====
${jurisprudenciasContext}
  `.trim();
  
  return contextContent;
};